import { InteractionStatus } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { useQuery } from '@tanstack/react-query'

import { getAccessToken } from '../auth/accessToken'
import { getRolesFromToken } from '../auth/tokenUtils'

interface UseUserRolesResult {
  roles: string[]
  hasRole: (role: string) => boolean
  isLoading: boolean
}

// Shared so callers memoising on `roles` don't recompute while roles are loading
const NO_ROLES: string[] = []

// Roles come from the access token's claims; React Query shares one result across every caller
export const useUserRoles = (): UseUserRolesResult => {
  const { instance, accounts, inProgress } = useMsal()
  const account = instance.getActiveAccount() ?? accounts[0]
  const isReady = inProgress === InteractionStatus.None

  const { data: roles = NO_ROLES, isPending } = useQuery({
    queryKey: ['userRoles', account?.homeAccountId],
    queryFn: async () => getRolesFromToken(await getAccessToken(instance, account)),
    enabled: isReady && !!account,
    // Roles only change with a new sign-in, which reloads the app
    staleTime: Infinity,
  })

  return {
    roles,
    hasRole: role => roles.includes(role),
    // Not loading once MSAL is idle and there is no account: that user simply has no roles
    isLoading: !isReady || (!!account && isPending),
  }
}
